import IntegrationsIconsSm from "@/assets/icons/IntegrationsIconsSm.svg";
import useUserData from "@/hooks/useUserData";

// =============================================================================
// Types
// =============================================================================

interface PlanFeature {
  label: string;
  value: string;
  soon?: boolean;
}

interface PlanConfig {
  title: string;
  price: string;
  priceColor?: string;
  features: PlanFeature[];
  actionLabel: string;
  actionHref?: string;
  isHighlighted?: boolean;
  badge?: string;
}

// =============================================================================
// Constants
// =============================================================================

const PLANS: PlanConfig[] = [
  {
    title: "Free for now ;)",
    price: "0€/mo",
    features: [
      { label: "Workspaces", value: "Unlimited" },
      { label: "Users per workspace", value: "Unlimited" },
      { label: "Integrations", value: "All" },
      { label: "AI usage", value: "Limited" },
    ],
    actionLabel: "Active",
  },
  {
    title: "One-time payment",
    price: "227€",
    priceColor: "#ffcc00",
    isHighlighted: true,
    badge: "7 left",
    features: [
      { label: "Workspaces", value: "Unlimited" },
      { label: "Users per workspace", value: "Unlimited" },
      { label: "Integrations", value: "All" },
      { label: "AI usage", value: "Unlimited" },
      { label: "24/7 Support", value: "Yes" },
      { label: "Added to fame wall", value: "Yes" },
      { label: "Advanced chat", value: "Yes", soon: true },
      { label: "Record with facecam", value: "Yes", soon: true },
      { label: "Branded workspace", value: "Yes", soon: true },
      { label: "Enterprise security", value: "Yes", soon: true },
      { label: "Advanced AI models", value: "Yes", soon: true },
    ],
    actionLabel: "Support us!",
    actionHref: "https://buy.stripe.com/aFa9AU1BO5NUe8y9wP6AM02",
  },
];

// =============================================================================
// Components
// =============================================================================

function FeatureRow({
  label,
  value,
  isAlternate,
  soon,
}: {
  label: string;
  value: string;
  isAlternate: boolean;
  soon?: boolean;
}) {
  return (
    <div
      className={`${
        isAlternate ? "bg-white/[0.04]" : ""
      } h-8 rounded-xl w-full flex items-center px-3 text-sm font-medium tracking-tight`}
    >
      <span className="flex-1 text-white/[0.32]">{label}</span>
      <span className="flex-1 text-[#eee] flex items-center gap-1.5">
        {value}
        {label === "Integrations" && (
          <img src={IntegrationsIconsSm} alt="Integrations" />
        )}
        {soon && <span className="text-white/[0.32]">(Soon)</span>}
      </span>
    </div>
  );
}

function FeatureList({ features }: { features: PlanFeature[] }) {
  return (
    <div className="flex flex-col gap-0.5 w-full">
      {features.map((feature, index) => (
        <FeatureRow
          key={feature.label}
          label={feature.label}
          value={feature.value}
          isAlternate={index % 2 === 0}
          soon={feature.soon}
        />
      ))}
    </div>
  );
}

function PlanHeader({
  title,
  price,
  priceColor = "white/[0.32]",
  badge,
}: {
  title: string;
  price: string;
  priceColor?: string;
  badge?: string;
}) {
  return (
    <div className="flex justify-between items-start w-full p-2">
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-white/[0.32] tracking-tight">
          {title}
        </p>
        <p
          className="text-[32px] font-medium tracking-tight leading-10"
          style={{ color: priceColor }}
        >
          {price}
        </p>
      </div>
      {badge && (
        <div className="h-5 px-1.5 bg-yellow-alpha/[0.16] text-yellow-600 border border-yellow-alpha/[0.08] rounded-lg flex items-center text-xs">
          {badge}
        </div>
      )}
    </div>
  );
}

function ActionButton({
  children,
  href,
  isPrimary,
}: {
  children: React.ReactNode;
  href?: string;
  isPrimary?: boolean;
}) {
  const baseClasses =
    "h-9 rounded-xl w-full flex items-center justify-center transition-colors";
  const variantClasses = isPrimary
    ? "bg-[rgba(255,204,0,0.16)] hover:bg-[rgba(255,204,0,0.24)] text-[#ffcc00]"
    : "bg-white/[0.04] text-white/[0.32]";

  const content = (
    <span className="text-sm font-medium tracking-tight">{children}</span>
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`${baseClasses} ${variantClasses}`}
      >
        {content}
      </a>
    );
  }

  return <div className={`${baseClasses} ${variantClasses}`}>{content}</div>;
}

function PlanCard({ plan }: { plan: PlanConfig }) {
  const { isHighlighted } = plan;

  return (
    <div
      className={`${
        isHighlighted ? "bg-[#1c1c1c]" : ""
      } overflow-hidden flex flex-col gap-5 p-5 rounded-3xl w-[400px] relative`}
    >
      {isHighlighted && (
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.08] z-[5]"
          style={{
            background:
              "linear-gradient(to bottom, transparent 50%, #FFCC00 100%)",
          }}
        />
      )}
      <PlanHeader
        title={plan.title}
        price={plan.price}
        priceColor={plan.priceColor}
        badge={plan.badge}
      />
      <FeatureList features={plan.features} />
      {plan.actionLabel && (
        <div className="mt-auto">
          <ActionButton href={plan.actionHref} isPrimary={isHighlighted}>
            {plan.actionLabel}
          </ActionButton>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export default function Plans() {
  const { user } = useUserData();
  const hasOneTimePlan = user?.planType === "one_time";

  const getAdjustedPlan = (plan: PlanConfig): PlanConfig => {
    if (!hasOneTimePlan) return plan;

    if (plan.isHighlighted) {
      return { ...plan, actionLabel: "Active", actionHref: undefined };
    }
    return {
      ...plan,
      actionLabel: "Thank you for leveling up!",
      actionHref: undefined,
    };
  };

  return (
    <div className="relative onest-md flex items-center justify-center w-full h-[calc(100vh-80px)]">
      <div className="flex gap-4 items-stretch p-1 rounded-[28px]">
        {PLANS.map((plan) => (
          <PlanCard key={plan.title} plan={getAdjustedPlan(plan)} />
        ))}
      </div>
    </div>
  );
}
